import { useId, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import framework from 'utils/framework';
const { wrapFunctionalComponent } = framework;
import LanguageLocaleUtils from 'utils/LanguageLocale';
import {
    Box, Text, Button, Link
} from 'components/ui';
import InputEmail from 'components/Inputs/InputEmail/InputEmail';
import Chevron from 'components/Chevron/Chevron';
import Storage from 'utils/localStorage/Storage';
import LOCAL_STORAGE from 'utils/localStorage/Constants';
import store from 'Store';
import Actions from 'Actions';
import UserActions from 'actions/UserActions';
import UrlUtils from 'utils/Url';
import {
    colors, space, fontSizes, lineHeights, fontWeights
} from 'style/config';
import ResetPasswordEN from 'pages/Auth/locales/ResetPassword_en_US';
import ResetPasswordFR from 'pages/Auth/locales/ResetPassword_fr_CA';

const POS_ACCOUNT_ERROR = 'ERR_400004';
// Reset-email-send failure signal (SIM-1 / ACS-4125 — AC-040). Placeholder
// pending backend confirmation of the exact code string; update in one place
// once backend publishes it. Detected here so the FE can keep the user on
// /reset-password and render the inline AC-040 message rather than dispatching
// the generic info modal.
const EMAIL_SEND_FAILED_CODE = 'ERR_RESET_EMAIL_SEND_FAILED';
const getText = LanguageLocaleUtils.getLocalisedDataFromResource(ResetPasswordEN, ResetPasswordFR);

/**
 * Reset Password Page
 * Full-screen password reset request page where users can request a password reset link
 *
 * Flow:
 * 1. User enters email address
 * 2. Submit email to backend reset password API endpoint
 * 3. On success: show success message modal
 * 4. User receives email with reset link
 */
const ResetPassword = () => {
    const emailInput = useRef(null);
    // React 18 stable id; keeps input/label associations unique across
    // concurrent instances (e.g. tests that mount the page twice).
    const emailFieldId = useId();
    // SUB-TASK-2 / RP-3: double-submit guard. Disables the Send Email button
    // while forgotPassword is in flight and is reset by BOTH success and
    // failure callbacks so retry works after a failure.
    const [isSubmitting, setIsSubmitting] = useState(false);
    // AC-040 / RP-1: inline failure message rendered above the form via
    // role="alert". Distinct from the modal-driven info/CheckYourEmail flows;
    // this branch keeps the user on /reset-password.
    const [sendFailureMessage, setSendFailureMessage] = useState('');

    const isValid = () => {
        const loginError = emailInput.current.validateError();

        return !loginError;
    };

    const dispatchShowInfoModal = errorMsg => {
        const email = emailInput.current.getValue();

        if (errorMsg) {
            store.dispatch(
                Actions.showInfoModal({
                    isOpen: true,
                    title: getText('resetPassword'),
                    message: errorMsg,
                    buttonText: getText('confirmButton'),
                    bodyPaddingBottom: 4,
                    isHtml: true
                })
            );
        } else {
            store.dispatch(Actions.showResetPasswordConfirmationModal({ isOpen: true, email, onDismiss: handleClose }));
        }
    };

    const submit = e => {
        e.preventDefault();

        const email = emailInput.current.getValue();

        const successCallback = () => {
            setIsSubmitting(false);
            dispatchShowInfoModal();
        };

        const failureCallback = async response => {
            const errorMsg = response.errorMessage || (response.errors || [])[0]?.errorMessage || getText('fallbackErrorMsg');
            const errorCode = (response.errors || [])[0]?.errorCode;

            // Reset the isSubmitting guard + surface the failure UI synchronously
            // so the DOM reflects the failure the moment the callback returns.
            // flushSync guarantees the commit even when the callback is
            // triggered outside a React event / act() boundary.
            flushSync(() => {
                setIsSubmitting(false);

                if (errorCode === EMAIL_SEND_FAILED_CODE) {
                    // AC-040: keep the user on the page and render the AC-040 copy
                    // inline via role="alert". Do NOT dispatch showInfoModal here
                    // (that would navigate the user away from the failure UX).
                    // BR-021: no console.* or dispatch payload may carry the email.
                    setSendFailureMessage(getText('emailSendFailed'));
                }
            });

            if (errorCode === EMAIL_SEND_FAILED_CODE) {
                return;
            }

            if (errorCode === POS_ACCOUNT_ERROR) {
                // BR-021 / RP-9: `email` intentionally omitted from the action
                // payload. CheckYourEmailModal sources the email from the
                // Redux user slice instead of receiving it via dispatch.
                store.dispatch(Actions.showCheckYourEmailModal({ isOpen: true, isResetPasswordFlow: true }));
            } else {
                dispatchShowInfoModal(errorMsg);
            }
        };

        if (isValid()) {
            // Clear any prior failure message and enter the in-flight state
            // BEFORE dispatching so the disabled= assertion between the click
            // and the async failureCallback sees isSubmitting=true (RP-3).
            setSendFailureMessage('');
            setIsSubmitting(true);
            store.dispatch(UserActions.forgotPassword(email, successCallback, json => failureCallback(json), null));
        }
    };

    const handleClose = () => {
        const externalAuthParams = Storage.local.getItem(LOCAL_STORAGE.EXTERNAL_AUTH_PARAMS);
        let redirectUrl = '/login';

        if (externalAuthParams) {
            /* eslint-disable camelcase */
            const oauthParams = {
                response_type: externalAuthParams.responseType,
                client_id: externalAuthParams.clientId,
                redirect_uri: externalAuthParams.redirectUri,
                scope: externalAuthParams.scope,
                state: externalAuthParams.state,
                code_challenge: externalAuthParams.codeChallenge,
                code_challenge_method: externalAuthParams.codeChallengeMethod,
                nonce: externalAuthParams.nonce,
                prompt: externalAuthParams.prompt,
                display: externalAuthParams.display,
                max_age: externalAuthParams.maxAge,
                ui_locales: externalAuthParams.uiLocales,
                id_token_hint: externalAuthParams.idTokenHint,
                login_hint: externalAuthParams.loginHint,
                acr_values: externalAuthParams.acrValues
            };
            /* eslint-enable camelcase */

            // Filter out empty values
            const filteredParams = Object.fromEntries(Object.entries(oauthParams).filter(([, value]) => value));

            const queryString = new URLSearchParams(filteredParams).toString();

            if (queryString) {
                redirectUrl = `/login?${queryString}`;
            }
        }

        UrlUtils.redirectTo(redirectUrl);
    };

    return (
        <div>
            <Box
                minHeight='100vh'
                display='flex'
                flexDirection='column'
                alignItems='center'
                css={styles.pageContainer}
            >
                {/* Back Button */}
                <Box
                    css={styles.backButton}
                    onClick={handleClose}
                >
                    <Chevron
                        direction={'left'}
                        color={colors.black}
                        css={styles.chevron}
                    />
                </Box>

                {/* Logo */}
                <Box css={styles.logoContainer}>
                    <img
                        src='/img/ufe/sephora-logo.svg'
                        alt='Sephora'
                        style={styles.logo}
                    />
                </Box>

                {/* Page Title */}
                <Box
                    maxWidth='400px'
                    width='100%'
                    css={styles.titleContainer}
                >
                    <Text
                        is='h1'
                        fontSize={fontSizes.lg}
                        fontWeight={fontWeights.bold}
                        lineHeight={lineHeights.tight}
                    >
                        {getText('pageTitle')}
                    </Text>
                </Box>

                {/* Description */}
                <Box
                    maxWidth='400px'
                    width='100%'
                    css={styles.descriptionContainer}
                >
                    <Text
                        fontSize={fontSizes.md}
                        textAlign='center'
                        lineHeight={lineHeights.base}
                    >
                        {getText('pageDescription')}
                    </Text>
                </Box>

                {/* Form Container with 400px max-width */}
                <Box
                    maxWidth='400px'
                    width='100%'
                >
                    {/* AC-040 inline failure message. Rendered as role="alert"
                        so assistive tech announces it (RP-5). Not rendered
                        when there is no failure message so RP-6 / RP-7 pass. */}
                    {sendFailureMessage ? (
                        <Box
                            role='alert'
                            id='reset-email-error'
                            css={styles.failureAlert}
                        >
                            {sendFailureMessage}
                        </Box>
                    ) : null}

                    {/* Reset Password Form */}
                    <form
                        noValidate
                        onSubmit={submit}
                    >
                        <Box css={styles.inputContainer}>
                            <InputEmail
                                id={emailFieldId}
                                label={getText('emailPlaceholder')}
                                ref={c => {
                                    if (c !== null) {
                                        emailInput.current = c;
                                    }
                                }}
                                autoFocus={true}
                                hideAsterisk={true}
                            />
                        </Box>

                        {/* Send Reset Link Button */}
                        <Button
                            type='submit'
                            width='100%'
                            variant='primary'
                            paddingY={space[3]}
                            disabled={isSubmitting}
                        >
                            {getText('sendResetLinkButton')}
                        </Button>
                    </form>

                    {/* Help Section */}
                    <Box css={styles.helpSection}>
                        <Text
                            fontSize={fontSizes.xs}
                            color={colors.gray}
                            lineHeight={lineHeights.base}
                            textAlign='center'
                        >
                            <b>{getText('stillHavingTrouble')}</b>
                            <br />
                            {getText('unableToResetPassword')}{' '}
                            <Link
                                href='tel:1-877-737-4672'
                                color={colors.blue}
                                underline={true}
                            >
                                1-877-SEPHORA
                            </Link>{' '}
                            {getText('phoneNumber')}
                            {' | '}
                            {getText('forAssistance')}{' '}
                            <Link
                                color={colors.blue}
                                underline={true}
                                href='/beauty/accessibility'
                            >
                                {getText('accessibility')}
                            </Link>
                            .
                        </Text>
                    </Box>
                </Box>
                {/* End Form Container */}
            </Box>
        </div>
    );
};

const styles = {
    pageContainer: {
        paddingTop: space[7],
        paddingBottom: space[7],
        paddingLeft: space[3],
        paddingRight: space[3],
        backgroundColor: colors.white,
        position: 'relative'
    },
    backButton: {
        position: 'absolute',
        top: space[5],
        left: space[5],
        cursor: 'pointer'
    },
    chevron: {
        width: space[4],
        height: space[4]
    },
    logoContainer: {
        marginBottom: space[6]
    },
    logo: {
        height: space[5],
        width: 'auto'
    },
    titleContainer: {
        marginBottom: space[4]
    },
    descriptionContainer: {
        marginBottom: space[6]
    },
    inputContainer: {
        marginBottom: space[5]
    },
    helpSection: {
        marginTop: space[6]
    },
    // AC-023 / RP-4: apply the failure-alert color via an Emotion `css` rule
    // (not a `color` Box prop) so the color declaration is guaranteed to be
    // present in document.styleSheets for the deterministic contrast scan —
    // mirrors SignIn.f.jsx's `styles.heading`. colors.red (#cf112c) is the
    // app-standard error-text color (used across GlobalModals) and is
    // WCAG-AA on the white surface (~5.3:1).
    failureAlert: {
        marginBottom: space[4],
        color: colors.red
    }
};

export default wrapFunctionalComponent(ResetPassword, 'ResetPassword');
